import React from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { blogs } from '../utilities/blogs';

const BlogTemplate: React.FC = () => {
    const { id } = useParams();
    const blog = blogs.find(b => b.id === id);

    if (!blog) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <h1 className="text-2xl text-darkbrown">Blog not found</h1>
            </div>
        );
    }

    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const stagger = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <motion.article 
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto px-4 py-8 md:py-12"
        >
            <motion.header className="mb-8" {...fadeIn}>
                <motion.h1 
                    className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
                    style={{ color: blog.color }}
                >
                    {blog.title}
                </motion.h1>
              
            </motion.header>

            <motion.div 
                className="relative w-full h-64 md:h-96 mb-8 rounded-lg overflow-hidden"
                {...fadeIn}
            >
                <img 
                    src={blog.imageUrl} 
                    alt={blog.title}
                    className="object-cover w-full h-full"
                />
            </motion.div>

            <motion.div 
                variants={stagger}
                className="prose max-w-none"
            >
                <motion.section {...fadeIn} className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4 text-darkbrown">Highlights</h2>
                    <motion.ul variants={stagger} className="space-y-2">
                        {blog.content.highlights.map((highlight, index) => (
                            <motion.li 
                                key={index}
                                variants={fadeIn}
                                className="flex items-start gap-2"
                            >
                                <span className="text-golden-yellow">•</span>
                                {highlight}
                            </motion.li>
                        ))}
                    </motion.ul>
                </motion.section>

                <motion.section {...fadeIn} className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4 text-darkbrown">Key Points</h2>
                    <motion.div variants={stagger} className="space-y-4">
                        {blog.content.keyPoints.map((point, index) => (
                            <motion.p 
                                key={index}
                                variants={fadeIn}
                                className="text-charcol"
                            >
                                {point}
                            </motion.p>
                        ))}
                    </motion.div>
                </motion.section>

                <motion.section {...fadeIn} className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4 text-darkbrown">Conclusion</h2>
                    <motion.div variants={stagger} className="space-y-4">
                        {blog.content.conclusion.map((point, index) => (
                            <motion.p 
                                key={index}
                                variants={fadeIn}
                                className="text-charcol"
                            >
                                {point}
                            </motion.p>
                        ))}
                    </motion.div>
                </motion.section>

                <motion.section {...fadeIn} className="mt-12">
                    <h2 className="text-xl font-semibold mb-4 text-darkbrown">References</h2>
                    <motion.ul variants={stagger} className="space-y-2 text-sm text-charcol">
                        {blog.content.references.map((reference, index) => (
                            <motion.li 
                                key={index}
                                variants={fadeIn}
                            >
                                {reference}
                            </motion.li>
                        ))}
                    </motion.ul>
                </motion.section>

                <motion.div 
                    className="mt-8 flex flex-wrap gap-2"
                    {...fadeIn}
                >
                    {blog.tags.map((tag, index) => (
                        <span 
                            key={index}
                            className="px-3 py-1 rounded-full text-sm"
                            style={{ 
                                backgroundColor: blog.color,
                                color: '#EBF1F1'
                            }}
                        >
                            {tag}
                        </span>
                    ))}
                </motion.div>
            </motion.div>
        </motion.article>
    );
};

export default BlogTemplate;